import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './BaseTool.css';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';
import api from '../api';
import RatingComponent from './RatingComponent';

const StyleTool = () => {
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [styleSample, setStyleSample] = useState('');
    const [averageRating, setAverageRating] = useState(0);
    const [ratingCount, setRatingCount] = useState(0);

    // Tool ID for rating component
    const toolId = 'style';

    useEffect(() => {
        // Fetch ratings for schema.org
        const fetchRatings = async () => {
            try {
                const response = await api.get(`/api/ratings/${toolId}`);
                setAverageRating(response.data.average || 0);
                setRatingCount(response.data.count || 0);
            } catch (err) {
                console.error('Error fetching ratings for schema:', err);
                // Use default values in case of error
                setAverageRating(0);
                setRatingCount(0);
            }
        };

        fetchRatings();
    }, [toolId]);

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "AI Style Matching Tool",
        "headline": "Rewrite Your Content in Any Writing Style with AI – Instant Style Matching",
        "description": "Paste a writing sample and get AI-generated text that matches its style. Perfect for branding, ghostwriting, and consistent content tone.",
        "url": "https://bookwitch.io/tools/style",
        "applicationCategory": "WritingApplication",
        "operatingSystem": "Web browser",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "featureList": "Style mimicry based on samples, Authentic voice preservation, Multiple use cases",
        "screenshot": "https://bookwitch.io/images/style-tool-screenshot.jpg",
        "creator": {
            "@type": "Organization",
            "name": "Book Witch",
            "url": "https://bookwitch.io"
        },
        "potentialAction": {
            "@type": "UseAction",
            "target": "https://bookwitch.io/tools/style"
        }
    };

    const handleProcess = async () => {
        if (!inputText.trim()) return;

        setIsLoading(true);
        try {
            const response = await api.post('/tools/style', {
                content: inputText,
                style_sample: styleSample
            });
            setOutputText(response.data.modified_content);
        } catch (error) {
            console.error('Style error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Rewrite Your Content in Any Writing Style with AI – Instant Style Matching</title>
                <meta name="description" content="Paste a writing sample and get AI-generated text that matches its style. Perfect for branding, ghostwriting, and consistent content tone." />


                <meta name="keywords" content="style matching tool, writing style adapter, tone matcher, voice consistency tool, writing style AI, style mimicry, voice adaptation" />

                <meta property="og:type" content="website" />
                <meta property="og:title" content="Match Any Writing Style with AI – Instantly Rewrite to Fit Your Voice | Book Witch" />
                <meta property="og:description" content="Need your content to sound more like you — or someone else? Paste a style sample and let our AI style matching tool rewrite your text to perfectly fit that tone, rhythm, and structure." />
                <meta property="og:url" content="https://bookwitch.io/tools/style" />

                <link rel="canonical" href="https://bookwitch.io/tools/style" />
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            </Helmet>

            <Navbar />
            <div className="base-tool">
                <div className="bt-header">
                    <h1>Match Any Writing Style with AI – Instantly Rewrite to Fit Your Voice</h1>
                    <p>Need your content to sound more like you — or someone else? Paste a style sample and let our AI style matching tool rewrite your text to perfectly fit that tone, rhythm, and structure.</p>
                </div>

                <div className="bt-features">
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">style</span>
                        <h3>Style mimicry based on real samples</h3>
                        <p>Feed the AI with your own writing, and it adapts like a chameleon</p>
                    </div>
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">psychology</span>
                        <h3>Authentic voice</h3>
                        <p>Keep your message, but change the tone to suit your audience</p>
                    </div>
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">auto_awesome</span>
                        <h3>Multiple use cases</h3>
                        <p>Great for ghostwriting, brand voice alignment, or multi-author content</p>
                    </div>
                </div>

                <div className="bt-editor">
                    <textarea
                        className="textarea"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="Enter your text here..."
                    />
                    <div className="bt-controls">
                        <textarea
                            className="textarea"
                            value={styleSample}
                            onChange={(e) => setStyleSample(e.target.value)}
                            placeholder="Paste a sample of the writing style you want to match..."
                        />
                        <button
                            onClick={handleProcess}
                            disabled={isLoading || !inputText.trim()}
                            className="button-important"
                        >
                            <div className="inner">
                                {isLoading ? 'Processing...' : 'Style Match'}
                            </div>
                        </button>
                    </div>
                </div>

                {outputText && (
                    <div className="bt-output">
                        <textarea
                            className="textarea"
                            value={outputText}
                            readOnly
                        />
                        <button
                            onClick={() => navigator.clipboard.writeText(outputText)}
                            className="button-hollow"
                        >
                            <span className="material-symbols-outlined">content_copy</span>
                            Copy
                        </button>
                    </div>
                )}

                <RatingComponent toolId={toolId} />

                <section className="bt-section">
                    <div className="bt-section-header">
                        <h2>How it works</h2>
                    </div>

                    <div className="bt-use-cases-grid">
                        <div className="bt-use-case-card">
                            <h3>1. Paste the content you want to rewrite</h3>
                            <p>Add your text to the first box.</p>
                        </div>
                        <div className="bt-use-case-card">
                            <h3>2. Paste a writing sample</h3>
                            <p>Add a sample that represents the style you want.</p>
                        </div>
                        <div className="bt-use-case-card">
                            <h3>3. Get results</h3>
                            <p>Click <strong>Style Match</strong> and get a perfectly adapted version.</p>
                        </div>
                    </div>
                </section>

                <section className="bt-cta">
                    <h2>Want to use your unique voice to generate full-length content?</h2>
                    <p>Try Book Witch and create books, guides, or training materials in your custom style.</p>
                    <p>Your voice. Your book. Your way.</p>
                    <div className="bt-cta-buttons">
                        <Link to="/app" className="no-style-link">
                            <button className="button-important">
                                <div className="inner">
                                    Try Book Witch
                                    <span className="material-symbols-outlined">arrow_forward</span>
                                </div>
                            </button>
                        </Link>
                    </div>
                </section>

                <section className="bt-faq">
                    <h2>FAQ – AI Style Matching</h2>
                    <div className="bt-faq-item">
                        <h3>How much text do I need to provide as a style sample?</h3>
                        <p>For best results, we recommend providing at least 2-3 paragraphs of text in the style you want to match. More sample text typically leads to better style mimicry.</p>
                    </div>
                    <div className="bt-faq-item">
                        <h3>Will this tool change the meaning of my content?</h3>
                        <p>No, our style matching tool aims to preserve the core meaning and message of your content while changing the tone, structure, and stylistic elements to match your desired style.</p>
                    </div>
                    <div className="bt-faq-item">
                        <h3>Can I use this for corporate branding?</h3>
                        <p>Absolutely! Many businesses use our style tool to ensure all content matches their brand voice, especially when multiple authors are creating content for the same brand.</p>
                    </div>
                </section>
            </div>
            <Footer />
        </>
    );
};

export default StyleTool; 